import { useEffect, useRef } from 'react'

import { SHORTCUTS, ShortcutId } from '@/constants/shortcuts'

const isTyping = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))

const comboOf = (e: KeyboardEvent) => (e.shiftKey && e.key.length > 1 ? `Shift+${e.key}` : e.key)

/**
 * Binds the POS shortcuts. Function keys work everywhere, even while the search box has focus; single characters
 * (`/`, `?`, `+`, `-`) only fire when the cashier is not typing. Paused while a dialog is open.
 */
export const useHotkeys = (handlers: Partial<Record<ShortcutId, () => void>>, enabled = true) => {
  const ref = useRef(handlers)
  ref.current = handlers

  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      const combo = comboOf(e)
      for (const [id, { keys }] of Object.entries(SHORTCUTS)) {
        const handler = ref.current[id as ShortcutId]
        if (!handler || !(keys as readonly string[]).includes(combo)) continue
        if (combo.length === 1 && isTyping(e.target)) return
        e.preventDefault()
        handler()
        return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])
}
